/**
 * Entra ID (Microsoft SSO) for portalen. Aktiveres kun når VITE_AAD_CLIENT_ID
 * og VITE_AAD_TENANT_ID er satt (dvs. i produksjonsbygget). I lokal utvikling
 * er de tomme → auth er av, og kall går mot lokal func via Vite-proxy.
 */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from "@azure/msal-browser";

const clientId = import.meta.env.VITE_AAD_CLIENT_ID as string | undefined;
const tenantId = import.meta.env.VITE_AAD_TENANT_ID as string | undefined;

export const authEnabled = Boolean(clientId && tenantId);
const apiScope = clientId ? `api://${clientId}/access_as_user` : "";

let msal: PublicClientApplication | null = null;

// MSAL 5 sender alltid tilbake til redirectUri (forsiden) etter innlogging eller
// fornyet token – «tilbake til startsiden» finnes ikke lenger. Vi husker derfor
// siden rett før vi sender brukeren til Microsoft, og legger den tilbake i
// adressen før React Router starter (initAuth kjøres før render i main.tsx).
const RETUR_KEY = "gb-auth-retur";
const RETUR_MAKS_MS = 10 * 60 * 1000;

function huskSide(): void {
  try {
    const side = window.location.pathname + window.location.search + window.location.hash;
    sessionStorage.setItem(RETUR_KEY, JSON.stringify({ side, tid: Date.now() }));
  } catch {
    /* uten sessionStorage lander brukeren på forsiden, som før */
  }
}

export function gjenopprettSide(): void {
  let lagret: { side?: string; tid?: number } | null = null;
  try {
    lagret = JSON.parse(sessionStorage.getItem(RETUR_KEY) ?? "null");
    sessionStorage.removeItem(RETUR_KEY);
  } catch {
    return;
  }
  const side = lagret?.side;
  const fersk = typeof lagret?.tid === "number" && Date.now() - lagret.tid < RETUR_MAKS_MS;
  // Bare når vi faktisk kom tilbake fra Microsoft (landet på forsiden), og bare interne stier
  if (!side || !fersk || window.location.pathname !== "/") return;
  if (!side.startsWith("/") || side.startsWith("//") || side === "/") return;
  window.history.replaceState(null, "", side);
}

/** Initialiser MSAL og håndter ev. innloggings-redirect. */
export async function initAuth(): Promise<void> {
  if (!authEnabled) return;
  msal = new PublicClientApplication({
    auth: {
      clientId: clientId!,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  await msal.initialize();
  const result = await msal.handleRedirectPromise();
  if (result?.account) msal.setActiveAccount(result.account);
  if (!msal.getActiveAccount()) {
    const accounts = msal.getAllAccounts();
    if (accounts.length > 0) msal.setActiveAccount(accounts[0]);
  }
  gjenopprettSide();
}

/** Sørg for at brukeren er innlogget. Returnerer false hvis vi redirecter til login. */
export async function ensureSignedIn(): Promise<boolean> {
  if (!authEnabled || !msal) return true;
  if (msal.getActiveAccount()) return true;
  huskSide();
  await msal.loginRedirect({ scopes: [apiScope] });
  return false; // siden navigerer bort til Microsoft-login
}

/** Hent access-token for API-et (stille; faller til redirect ved behov). */
export async function getApiToken(): Promise<string | null> {
  if (!authEnabled || !msal) return null;
  const account = msal.getActiveAccount();
  if (!account) return null;
  try {
    const res = await msal.acquireTokenSilent({ scopes: [apiScope], account });
    return res.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      huskSide();
      await msal.acquireTokenRedirect({ scopes: [apiScope] });
    }
    return null;
  }
}

export function currentAccount(): AccountInfo | null {
  return msal?.getActiveAccount() ?? null;
}

/** Access-token for vilkårlige Microsoft Graph-scopes (stille; redirect ved behov). */
export async function getGraphToken(scopes: string[]): Promise<string | null> {
  if (!authEnabled || !msal) return null;
  const account = msal.getActiveAccount();
  if (!account) return null;
  try {
    const r = await msal.acquireTokenSilent({ scopes, account });
    return r.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      huskSide();
      await msal.acquireTokenRedirect({ scopes });
    }
    return null;
  }
}

/** Navn + e-post fra den innloggede kontoen (fra ID-token-claims). */
export function getProfile(): { name: string; email: string } | null {
  const acc = currentAccount();
  if (!acc) return null;
  return { name: acc.name ?? acc.username, email: acc.username };
}

/** Hent brukerens profilbilde fra Microsoft Graph som en object-URL (eller null). */
export async function getPhotoObjectUrl(): Promise<string | null> {
  if (!authEnabled || !msal) return null;
  const account = msal.getActiveAccount();
  if (!account) return null;
  let token: string;
  try {
    const r = await msal.acquireTokenSilent({ scopes: ["User.Read"], account });
    token = r.accessToken;
  } catch {
    return null; // ikke kritisk – vi faller tilbake til initialer
  }
  try {
    const res = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null; // 404 = intet bilde satt
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}

export function signOut(): void {
  if (msal) void msal.logoutRedirect();
}
